/**
 * Row + header rendering for the shared high-score table (leaderboard.js#renderLeaderboardInto's
 * consumer). Owns the column header, the click-toggleable row body, sort-aware metric cells, and
 * the scroll-on-expand behavior. A row's toggle affordance lives on `.leaderboard-row-head` — the
 * wrapper around the unexpanded content (rank/name/metric cells/recency), not the `li` itself
 * (leaderboard-row-stats-band-matrix design § A, the header-toggle restructure): a tap anywhere in
 * the expansion is inert for the toggle, and `[data-tooltip]` anchors inside it no longer sit under
 * a `[role=button]` ancestor — the fix for `tooltip.js`'s tap-to-reveal never firing inside a
 * leaderboard row on touch (that module itself is unchanged; the DOM was mislabeling a region as
 * button content). `toggleRow` still toggles `.expanded` on the `li` (the CSS state hook and
 * `scrollIntoView` anchor) and still fires `leaderboard-inspected` on expand only. Expansion-detail
 * content is the stats-v2 run panel fragment plus, above it, the stats-band matrix (stats-v2 panel
 * redesign design § Rendering & derivation; leaderboard-row-stats-band-matrix design § B) — the
 * fetch/mount/measured-accordion machinery split out to `leaderboard-expansion.js` (§ Structure &
 * SRP — a coherent second responsibility this slice was about to grow onto an already-yellow
 * module). Shared DOM and number helpers in {@code leaderboard-format.js}.
 *
 * Recency ("3h ago") is a presence signal, not a stat — it renders straight from
 * {@code row.lastSeen} into the row's head, alongside the always-present cells, so it survives
 * the row never being expanded (and thus the panel fragment never being fetched). Ported
 * verbatim from the pre-stats-v2 {@code game-start-leaderboard-detail.js} (git history), whose
 * teardown dropped it as unintended collateral — the seat-triple it stood beside retired for a
 * real reason (the fragment's standings row replaces it), but nothing replaces recency.
 *
 * Spec: specs/2026-05/2026-05-16-leaderboard-stats-and-tabs-design.md (row/header shape),
 * specs/2026-07/2026-07-17-stats-v2-panel-redesign-design.md (the expansion content),
 * specs/2026-08/2026-08-10-leaderboard-dedicated-page-design.md (mode-scoped cache, `source`),
 * specs/2026-08/2026-08-15-leaderboard-row-stats-band-matrix-design.md (head-toggle restructure,
 * the matrix mount).
 *
 * Names are rendered via textContent only — never innerHTML — because the response carries strings
 * supplied by unauthenticated POSTs from arbitrary visitors. The setName() helper localizes the
 * rule.
 */
import { t } from "../i18n.js";
import {
  exactDetail,
  formatCompact,
  formatCompactSigned,
} from "../compact-numbers.js";
import { createBalanceScale, createWorthChest } from "../icons/stat-icons.js";
import { createCountingFrame } from "../icons/prompt-icons.js";
import { createPersonaKnight } from "../icons/persona-icons.js";
import { track } from "../analytics.js";
import {
  adoptRetainedPanel,
  collapseDetail,
  expandDetail,
} from "./leaderboard-expansion.js";
import {
  el,
  formatAvg,
  formatRelative,
  setName,
} from "./leaderboard-format.js";

// play-test: "larger and bolder" (Andre) — the header wears no text at all anymore
// (leaderboard-header-show-dont-tell), so the marks are free to read at a size that
// carries the row on its own rather than sit small beside a label. Shared by all five
// header cells; the Knight gets its own +2px (below) to compensate its bust's smaller
// optical footprint against the four square marks.
const HEADER_ICON_SIZE = 32;
const HEADER_KNIGHT_ICON_SIZE = 34; // play-test: +2px — see HEADER_ICON_SIZE above

export function buildHeading() {
  const h = el("h3", "leaderboard-heading");
  setName(h, t("leaderboardHeading"));
  return h;
}

/**
 * The "nothing here" line. `monthLabel` switches it to the hall-of-fame wording (hall-of-fame
 * design § 3): "Be the first to play this mode" is an invitation, and a closed month cannot be
 * joined — its board is a finished record.
 */
export function buildEmptyNotice(monthLabel = null) {
  const text = monthLabel
    ? t("leaderboardEmptyMonth", monthLabel)
    : t("leaderboardEmpty");
  return el("div", "leaderboard-empty", text);
}

/**
 * The name-yourself nudge below a nameless viewer's own row (du-on-leaderboard design) — they play,
 * see their provisional rank, and are told exactly what closes the gap to a real one.
 */
export function buildNudge() {
  return el("div", "leaderboard-nudge", t("leaderboardNudgeName"));
}

export function buildLoading() {
  return el("div", "leaderboard-loading", "· · ·");
}

export function buildUnavailable() {
  return el("div", "leaderboard-unavailable", t("leaderboardUnavailable"));
}

export function buildSeparator() {
  return el("div", "leaderboard-separator", "· · ·");
}

/**
 * Column header row above the list. Same grid template as a row's head so the marks align with
 * the cells beneath. Every cell but rank speaks in an icon mark now (leaderboard-header-
 * show-dont-tell — the row expansion's grammar reaching the list header above it): Player
 * wears the Knight persona bust, Rounds the counting frame, Score the worth chest, Net and
 * avg-net the Bilanz balance scale — each cell's term rides its tooltip (`buildHeaderIconCell`)
 * plus an aria-label, since the icon itself is aria-hidden and would otherwise leave the cell
 * with no accessible name. The sort tabs above stay verbal — they're controls, not data, and
 * the panel's grammar has nothing to say about controls. The active-sort column lives
 * rightmost, carries {@code is-sort} (color, not weight — bold is a no-op on an SVG mark) as
 * the sort cue. Body cells stay uniform weight per the 2026-05-17 polish pass (no more
 * muted/bold/normal rotation per row).
 *
 * No ▼ chevron next to the active header mark — sort lives on the tab strip above, not on
 * column clicks, and a chevron suggested a clickable sort affordance that the table doesn't
 * actually provide (Andre 2026-05-17).
 */
export function buildHeader(sortBy) {
  const header = el("div", "leaderboard-header");
  header.appendChild(el("span", "leaderboard-rank", "#"));
  header.appendChild(
    buildHeaderIconCell(
      "leaderboard-name",
      createPersonaKnight(HEADER_KNIGHT_ICON_SIZE),
      t("leaderboardHeaderName"),
    ),
  );
  for (const cell of buildHeaderCells(sortBy)) header.appendChild(cell);
  return header;
}

function buildHeaderCells(sortBy) {
  const rounds = buildHeaderIconCell(
    "leaderboard-rounds",
    createCountingFrame(HEADER_ICON_SIZE),
    t("leaderboardStatRounds"),
  );
  const score = buildHeaderIconCell(
    "leaderboard-score",
    createWorthChest(HEADER_ICON_SIZE),
    t("leaderboardHeaderScore"),
  );
  const net = buildScaleHeaderCell("leaderboard-net", t("netLabel"));
  const avg = buildScaleHeaderCell(
    "leaderboard-avg-net",
    t("leaderboardHeaderAvgNet"),
  );
  if (sortBy === "AVG_NET_PER_ROUND") return [rounds, net, markActive(avg)];
  if (sortBy === "ROUNDS") return [net, score, markActive(rounds)];
  return [rounds, score, markActive(net)]; // NET (default)
}

function markActive(cell) {
  cell.classList.add("is-sort");
  return cell;
}

/**
 * The Bilanz scale for the Net/avg-net headers (number-glyphs sweep) — one mark teaches the
 * column, the rows keep bare aligned numbers; the term rides the app tooltip per TERMS'
 * out-of-game doctrine, plus an aria-label (buildHeaderIconCell). Superseded (leaderboard-
 * header-show-dont-tell): now the full-cut {@code createBalanceScale} at HEADER_ICON_SIZE, not
 * the chip cut — the family's own two-cut doctrine gives the full cut homes ≥~28px (stat-icons.js
 * file header) and the whole header is a home that size now. Score, once the header's last text
 * label because the family had no classic-score mark, wears the worth chest instead — its read
 * widened from "one round's Spielwert" to "the accumulated classic score", the sum of game
 * values banked (Andre approved this reading).
 */
function buildScaleHeaderCell(className, term) {
  return buildHeaderIconCell(
    className,
    createBalanceScale(HEADER_ICON_SIZE),
    term,
  );
}

/** One icon-only header cell: `icon` carries the column's meaning, `term` rides both the app
 *  tooltip (hover/long-press) and an aria-label — the icon itself stays aria-hidden, so without
 *  the aria-label the cell would have no accessible name at all (TERMS' tooltip-is-
 *  description-not-name doctrine). */
function buildHeaderIconCell(className, icon, term) {
  const cell = el("span", className);
  cell.dataset.tooltip = term;
  cell.setAttribute("aria-label", term);
  icon.classList.add("leaderboard-header-icon");
  icon.setAttribute("aria-hidden", "true");
  cell.appendChild(icon);
  return cell;
}

/**
 * One leaderboard row + its click-toggle expansion. The `li` carries exactly two children: a
 * `.leaderboard-row-head` wrapping the unexpanded content (rank, name, metric cells, recency) —
 * the toggle affordance's real box (design § A: `display: contents` can't take focus outlines or
 * hover backgrounds) — and the empty `.leaderboard-detail` expansion slot. A nameless viewer's row
 * (never possible for a non-viewer row — the server excludes nameless entries from topRows) renders
 * the pronoun client-side via i18n ("Du"/"You") instead of the empty-name placeholder: the
 * identity/board name is a nullable fact the server carries honestly, but the self-facing surface
 * still owes the viewer a name for their own row (du-on-leaderboard design). The expansion slot
 * starts empty — its content is the stats-v2 run panel fragment plus the stats-band matrix, fetched
 * on first expand (stats-v2 panel redesign design § Rendering & derivation; leaderboard-row-stats-
 * band-matrix design § B; see the file header) — unless this board already holds the viewer's panel
 * for the view being rendered, in which case that mounted, enhanced node is adopted as the slot
 * (leaderboard-expansion.js#adoptRetainedPanel) instead of a fresh one being built and filled.
 */
export function buildRow(row, rankText, sortBy, settings, container) {
  const li = el("li", "leaderboard-row" + (row.isViewer ? " is-viewer" : ""));
  li.dataset.sortBy = sortBy;
  const head = buildRowHead(row, rankText, sortBy);
  li.appendChild(head);
  const retained = row.isViewer
    ? adoptRetainedPanel(row, settings, container)
    : null;
  const detail = retained ?? el("div", "leaderboard-detail");
  // Mirror the visual collapse for screen readers — without this, the SR announces the detail as
  // part of the row's accessible name even when visually hidden via max-height: 0.
  detail.setAttribute("aria-hidden", "true");
  li.appendChild(detail);
  wireRowToggle(head, li, row, settings, container);
  // Viewer's own row opens by default so visitors land on their numbers without a click,
  // and the visible expansion teaches "there's more here, click around" for other rows.
  // Apply state directly rather than toggleRow() — toggleRow fires scrollIntoView and we
  // don't want the initial game-start render to jump-scroll on entry.
  if (row.isViewer) {
    li.classList.add("expanded");
    head.setAttribute("aria-expanded", "true");
    detail.setAttribute("aria-hidden", "false");
    // Only a fresh slot expands. A retained panel arrives mounted, enhanced and already settled
    // open, so this auto-open — the one that fires on every board rebuild without the visitor
    // touching anything — costs nothing at all. A fresh slot's auto-open IS the row's first expand
    // and still needs the whole measured-max-height + fragment-fetch sequence toggleRow's expand
    // path drives (expandDetail, leaderboard-expansion.js).
    if (retained == null) expandDetail(detail, li, row, settings, container);
  }
  return li;
}

/** The row's head: the unexpanded content (rank, name, metric cells, recency) that `wireRowToggle`
 *  then turns into the row's toggle affordance — the file header carries why that box, and not the
 *  `li`, is the one that carries it. */
function buildRowHead(row, rankText, sortBy) {
  const head = el("div", "leaderboard-row-head");
  head.appendChild(el("span", "leaderboard-rank", rankText));
  const nameEl = el("span", "leaderboard-name");
  setName(
    nameEl,
    row.isViewer && !row.name ? t("leaderboardYouPronoun") : row.name || "—",
  );
  head.appendChild(nameEl);
  for (const cell of buildMetricCells(row, sortBy)) head.appendChild(cell);
  const recency = buildRecencyLine(row);
  if (recency) head.appendChild(recency);
  return head;
}

/** Renders "3h ago" beside the row's own cells — null when the entry carries no {@code
 *  lastSeen} (never happens on the live wire today, but the old code guarded it and the
 *  contract doesn't promise otherwise). Grid-spans the full head width; CSS keeps it muted
 *  and small so it reads as ambient metadata, not another stat. */
function buildRecencyLine(row) {
  if (row.lastSeen == null) return null;
  const line = el("div", "leaderboard-row-recency");
  line.textContent = formatRelative(row.lastSeen, new Date().toISOString());
  return line;
}

/**
 * Three metric cells per tab in display order. The active-sort cell is rightmost (2026-05-17
 * polish pass — header chevron + position together teach the sort key). The CSS hides
 * non-active cells on narrow viewports so the row collapses to {@code [rank, name, active]}.
 */
function buildMetricCells(row, sortBy) {
  if (sortBy === "AVG_NET_PER_ROUND") {
    return [
      buildRoundsCell(row, false),
      buildNetCell(row, false),
      buildAvgNetCell(row, true),
    ];
  }
  if (sortBy === "ROUNDS") {
    return [
      buildNetCell(row, false),
      buildScoreCell(row, false),
      buildRoundsCell(row, true),
    ];
  }
  // NET (default)
  return [
    buildRoundsCell(row, false),
    buildScoreCell(row, false),
    buildNetCell(row, true),
  ];
}

function buildRoundsCell(row, isActive) {
  return el(
    "span",
    "leaderboard-rounds" + activeClass(isActive),
    formatCompact(row.roundsPlayed),
  );
}

function buildAvgNetCell(row, isActive) {
  const cell = el("span", "leaderboard-avg-net" + activeClass(isActive));
  cell.textContent =
    row.roundsPlayed === 0 ? "—" : formatAvg(row.bilanz / row.roundsPlayed);
  return cell;
}

function buildNetCell(row, isActive) {
  const cell = el(
    "span",
    "leaderboard-net" + activeClass(isActive),
    formatCompactSigned(row.bilanz),
  );
  cell.dataset.tooltip = t("netExplainer");
  // The one board cell that both compacts (sig-digit-numbers task) and already had a tooltip
  // anchor, so the exact figure joins its detail rather than disappearing with the digits. Null
  // below 1000, where the cell already shows the exact value — a detail repeating it is noise.
  const exact = exactDetail(row.bilanz);
  if (exact != null) cell.dataset.tooltipDetail = exact;
  return cell;
}

function buildScoreCell(row, isActive) {
  return el(
    "span",
    "leaderboard-score" + activeClass(isActive),
    formatCompactSigned(row.humanScore),
  );
}

function activeClass(isActive) {
  return isActive ? " is-sort" : " is-secondary";
}

/** Wires the toggle affordance onto `head` — role/tabindex/aria-expanded, click + Enter/Space
 *  keydown — rather than `li` (design § A): a tap/click anywhere in the expansion is now inert
 *  for the toggle, and no `[role=button]` ancestor sits over `[data-tooltip]` anchors inside the
 *  detail, which is what unblocks `tooltip.js`'s tap-to-reveal for the whole expansion on touch. */
function wireRowToggle(head, li, row, settings, container) {
  head.setAttribute("role", "button");
  head.setAttribute("tabindex", "0");
  head.setAttribute("aria-expanded", "false");
  head.addEventListener("click", () =>
    toggleRow(head, li, row, settings, container),
  );
  head.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleRow(head, li, row, settings, container);
    }
  });
}

function toggleRow(head, li, row, settings, container) {
  const expanded = li.classList.toggle("expanded");
  head.setAttribute("aria-expanded", expanded ? "true" : "false");
  // Always present: `buildRow` appends the slot before it wires this handler, so there is no
  // row whose head can be clicked without one.
  const detail = li.querySelector(".leaderboard-detail");
  detail.setAttribute("aria-hidden", expanded ? "false" : "true");
  if (!expanded) {
    collapseDetail(detail);
    return;
  }
  // fire on expand only, never on collapse; `source` (start/page/overlay) reads the mount's own
  // data-lb-surface stamp (leaderboard-dedicated-page-design § Analytics) — the ancestor closest()
  // finds is whichever container renderLeaderboardInto was called on, since every mount stamps it.
  track("leaderboard-inspected", {
    source: li.closest("[data-lb-surface]")?.dataset.lbSurface,
  });
  if (typeof li.scrollIntoView !== "function") {
    expandDetail(detail, li, row, settings, container);
    return;
  }
  // JS scroll API doesn't auto-honor prefers-reduced-motion (unlike CSS transitions),
  // so check explicitly. The function guard above sidesteps jsdom (no scrollIntoView impl).
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const behavior = reduceMotion ? "auto" : "smooth";
  const scroll = () => li.scrollIntoView({ behavior, block: "nearest" });
  if (reduceMotion) {
    expandDetail(detail, li, row, settings, container);
    requestAnimationFrame(scroll);
    return;
  }
  // Full-motion path: scroll only once the expansion has genuinely settled — the mounted,
  // full-height panel — rather than on the first max-height transition's end/cancel, which on
  // the live page usually fires while the panel is still near-empty (the fragment mounts
  // asynchronously; see expandDetail's onSettled contract, leaderboard-expand-scroll-into-view-
  // never-fires). Re-check `.expanded` at fire time: a fast expand → collapse must not scroll a
  // row that has already been re-collapsed by the time the settle signal lands.
  expandDetail(detail, li, row, settings, container, () => {
    if (li.classList.contains("expanded")) scroll();
  });
}
